import { Avatar, Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { FileIcon, defaultStyles } from "react-file-icon";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { globalTranslate } from "../../../../shared/utils";

interface ModalDocumentValueListProps {
	documents: any[];
	onPreview: (doc: any) => void;
	onDelete: (doc: any) => void;
	documentTypes: any[];
	canDelete: boolean;
	canDeleteDoc?: (doc: any) => boolean;
	showDocTypeLabel?: boolean;
	showValueDebitLabel?: boolean;
}

export const ModalDocumentValueList = ({
	documents,
	onPreview,
	onDelete,
	documentTypes,
	canDelete,
	canDeleteDoc,
	showDocTypeLabel = true,
	showValueDebitLabel = false,
}: ModalDocumentValueListProps) => {
	const getFileExtension = (filename: string): string => {
		return filename?.split(".").pop()?.toLowerCase() || "file";
	};

	const getDocDisplayName = (doc: any) => {
		return doc?.originalName ?? doc?.originalFilename ?? doc?.filename ?? "Arquivo";
	};

	const getDocDate = (doc: any) => {
		return doc?.createdAt ?? doc?.uploadedDate;
	};

	const canDeleteThisDoc = (doc: any) => {
		if (typeof canDeleteDoc === "function") return canDeleteDoc(doc);
		return canDelete;
	};

	const getFileIconStyle = (filename: string) => {
		const ext = getFileExtension(filename);
		return defaultStyles[ext as keyof typeof defaultStyles] || {};
	};

	return (
		<>
			{documents.map((doc: any) => {
				const docTypeItem = documentTypes.find((item) => item.docType === doc.docType);
				const downloadUrl = doc?.url || docTypeItem?.url;
				const displayName = getDocDisplayName(doc);
				const createdAt = getDocDate(doc);
				const debitLabel = doc?.valueTypeId ? `${globalTranslate(doc.valueTypeId)} ${doc?.valueReference ?? ""}`.trim() : "";

				return (
					<Box
						key={doc.id}
						sx={{
							border: "1px solid #eee",
							borderRadius: 2,
							p: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Stack direction="row" spacing={2} alignItems="center">
							<Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: "transparent", p: 1 }}>
								<FileIcon extension={getFileExtension(displayName)} {...getFileIconStyle(displayName)} />
							</Avatar>

							<Box sx={{ minWidth: 0 }}>
								<Typography fontWeight={600}>{displayName}</Typography>
								{showDocTypeLabel && (
									<Typography variant="body2" color="text.secondary">
										{globalTranslate(doc.docType)}
									</Typography>
								)}
								{showValueDebitLabel && !!debitLabel && (
									<Typography variant="body2" color="text.secondary">
										{debitLabel}
									</Typography>
								)}
								{createdAt && (
									<Typography variant="caption" color="text.secondary">
										{format(new Date(createdAt), "dd/MM/yyyy HH'h'mm'm'", { locale: ptBR })}
									</Typography>
								)}
							</Box>
						</Stack>

						<Stack direction="row" spacing={1}>
							<Tooltip title="Visualizar">
								<IconButton size="large" onClick={() => onPreview(doc)}>
									<VisibilityIcon />
								</IconButton>
							</Tooltip>

							<Tooltip title="Baixar">
								<span>
									<IconButton size="large" component="a" href={downloadUrl} download disabled={!downloadUrl}>
										<DownloadIcon />
									</IconButton>
								</span>
							</Tooltip>

							{canDeleteThisDoc(doc) && (
								<Tooltip title="Excluir anexo">
									<IconButton size="large" onClick={() => onDelete(doc)}>
										<DeleteIcon color="error" />
									</IconButton>
								</Tooltip>
							)}
						</Stack>
					</Box>
				);
			})}
		</>
	);
};
